<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-32">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6 bg-white rounded-xl p-5">
        <div>
          <h1 class="text-xl font-bold text-gray-800 mb-1">Check-In abschließen</h1>
          <p class="text-sm text-gray-600">Wähle die Ziel-Location für das Fahrzeug</p>
        </div>
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-[#E9041D] opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-4">
          <div v-if="isLoading" class="py-8 flex flex-col items-center justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9041D] mb-4"></div>
            <p class="text-gray-600">Lade Locations...</p>
          </div>
          
          <div v-else-if="error" class="py-8 flex flex-col items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-red-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p class="text-gray-600">{{ error }}</p>
            <button 
              @click="router.push('/checks/start')" 
              class="mt-4 px-4 py-2 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Zurück zum Start
            </button>
          </div>
          
          <div v-else>
            <!-- Search Bar -->
            <div class="mb-4">
              <div class="relative">
                <input 
                  v-model="searchQuery"
                  type="text"
                  placeholder="Location suchen..."
                  class="w-full px-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E9041D] focus:border-[#E9041D] transition-colors"
                  @input="filterLocations"
                />
                <svg xmlns="http://www.w3.org/2000/svg" class="absolute left-3 top-3.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <button 
                  v-if="searchQuery"
                  @click="searchQuery = ''; filterLocations()"
                  class="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Recent Locations (if available) -->
            <div v-if="recentLocations.length > 0 && !searchQuery" class="mb-6">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Zuletzt verwendet</h3>
              <div class="space-y-2">
                <div 
                  v-for="location in recentLocations.slice(0, 3)"
                  :key="`recent-${location.id}`"
                  @click="selectedLocationId = location.id"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  :class="{ 'ring-2 ring-[#E9041D] bg-red-50': selectedLocationId === location.id }"
                >
                  <div class="flex items-center space-x-3">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p class="font-medium text-gray-800">{{ location.name }}</p>
                      <p class="text-xs text-gray-500">{{ getLocationTypeName(location.location_type) }}</p>
                    </div>
                  </div>
                  <div 
                    class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors"
                    :class="selectedLocationId === location.id ? 'border-[#E9041D] bg-[#E9041D]' : 'border-gray-300'"
                  >
                    <svg 
                      v-if="selectedLocationId === location.id"
                      xmlns="http://www.w3.org/2000/svg" 
                      class="h-4 w-4 text-white" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <!-- Grouped Locations -->
            <div v-if="!searchQuery" class="space-y-4">
              <!-- Servicecenter Group -->
              <div v-if="serviceLocations.length > 0">
                <div 
                  @click="toggleGroup('servicecenter')"
                  class="flex items-center justify-between py-2 cursor-pointer"
                >
                  <div class="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <h3 class="text-sm font-semibold text-gray-700">Servicecenter</h3>
                    <span class="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{{ serviceLocations.length }}</span>
                  </div>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    class="h-5 w-5 text-gray-400 transition-transform"
                    :class="{ 'rotate-180': expandedGroups.servicecenter }"
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <div v-show="expandedGroups.servicecenter" class="space-y-2 mt-2">
                  <LocationItem 
                    v-for="location in serviceLocations"
                    :key="location.id"
                    :location="location"
                    :is-selected="selectedLocationId === location.id"
                    @select="selectedLocationId = location.id"
                  />
                </div>
              </div>

              <!-- Drehplatz Group -->
              <div v-if="drehplatzLocations.length > 0">
                <div 
                  @click="toggleGroup('drehplatz')"
                  class="flex items-center justify-between py-2 cursor-pointer"
                >
                  <div class="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <h3 class="text-sm font-semibold text-gray-700">Drehplatz</h3>
                    <span class="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{{ drehplatzLocations.length }}</span>
                  </div>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    class="h-5 w-5 text-gray-400 transition-transform"
                    :class="{ 'rotate-180': expandedGroups.drehplatz }"
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <div v-show="expandedGroups.drehplatz" class="space-y-2 mt-2">
                  <LocationItem 
                    v-for="location in drehplatzLocations"
                    :key="location.id"
                    :location="location"
                    :is-selected="selectedLocationId === location.id"
                    @select="selectedLocationId = location.id"
                  />
                </div>
              </div>

              <!-- Parkplatz Group -->
              <div v-if="parkplatzLocations.length > 0">
                <div 
                  @click="toggleGroup('parkplatz')"
                  class="flex items-center justify-between py-2 cursor-pointer"
                >
                  <div class="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    <h3 class="text-sm font-semibold text-gray-700">Parkplätze</h3>
                    <span class="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">{{ parkplatzLocations.length }}</span>
                  </div>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    class="h-5 w-5 text-gray-400 transition-transform"
                    :class="{ 'rotate-180': expandedGroups.parkplatz }"
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                <div v-show="expandedGroups.parkplatz" class="space-y-2 mt-2">
                  <LocationItem 
                    v-for="location in parkplatzLocations"
                    :key="location.id"
                    :location="location"
                    :is-selected="selectedLocationId === location.id"
                    @select="selectedLocationId = location.id"
                  />
                </div>
              </div>
            </div>

            <!-- Search Results -->
            <div v-if="searchQuery" class="space-y-2">
              <p v-if="filteredLocations.length === 0" class="text-center py-4 text-gray-500">
                Keine Locations gefunden für "{{ searchQuery }}"
              </p>
              <LocationItem 
                v-for="location in filteredLocations"
                :key="location.id"
                :location="location"
                :is-selected="selectedLocationId === location.id"
                @select="selectedLocationId = location.id"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sticky Footer with Selected Location and Confirm Button -->
    <!-- Debug: Show data inconsistency warning if needed -->
    <div 
      v-if="hasDataButNoButton" 
      class="fixed bottom-20 left-4 right-4 bg-yellow-100 border border-yellow-400 text-yellow-800 p-3 rounded-lg z-40"
    >
      <p class="text-sm font-semibold">⚠️ Debug Warning:</p>
      <p class="text-xs">Locations are displayed but availableLocations is empty. Check console for details.</p>
    </div>
    
    <div 
      v-if="canShowCompleteButton"
      class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-20"
    >
      <div v-if="selectedLocation" class="mb-3 px-3 py-2 bg-gray-50 rounded-lg">
        <p class="text-xs text-gray-500">Ausgewählte Location:</p>
        <p class="font-medium text-gray-800">{{ selectedLocation.name }}</p>
      </div>
      <button
        @click="completeCheckIn"
        :disabled="isSubmitting || !selectedLocationId"
        class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-if="isSubmitting">Wird abgeschlossen...</span>
        <span v-else>Check-In abschließen</span>
      </button>
      <button
        type="button"
        @click="showCancelDialog = true"
        :disabled="isCancelling"
        class="w-full py-2 px-4 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors mt-2"
      >
        Check-In abbrechen
      </button>
    </div>

    <!-- Cancel Confirmation Dialog -->
    <div v-if="showCancelDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
        <h3 class="text-lg font-bold text-gray-800 mb-2">Check-In abbrechen?</h3>
        <p class="text-sm text-gray-600 mb-6">Der Check-In wird unwiderruflich gelöscht. Alle bisherigen Eingaben gehen verloren.</p>
        <div class="flex space-x-3">
          <button
            @click="showCancelDialog = false"
            class="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Zurück
          </button>
          <button
            @click="cancelCheckin(checkinIdNumber)"
            :disabled="isCancelling"
            class="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50"
          >
            {{ isCancelling ? 'Wird gelöscht...' : 'Abbrechen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '../api/axios'
import LocationItem from '../components/LocationItem.vue'
import { useCheckinHeartbeat } from '../composables/useCheckinHeartbeat'
import { useCancelCheckin } from '../composables/useCancelCheckin'

interface Location {
  id: number
  name: string
  location_type: {
    id: number
    name: string
    slug?: string
  }
  is_locked: boolean
}

const router = useRouter()
const route = useRoute()
const checkId = computed(() => route.params.id as string)
const checkinIdNumber = computed(() => parseInt(checkId.value))

// Heartbeat for connection monitoring
const {
  startHeartbeat,
  stopHeartbeat
} = useCheckinHeartbeat(checkinIdNumber)

const { isCancelling, showCancelDialog, cancelCheckin } = useCancelCheckin(stopHeartbeat)

const isLoading = ref(true)
const isSubmitting = ref(false)
const error = ref<string | null>(null)
const availableLocations = ref<Location[]>([])
const selectedLocationId = ref<number | null>(null)
const searchQuery = ref('')
const filteredLocations = ref<Location[]>([])
const recentLocations = ref<Location[]>([])

// Grouped location states
const expandedGroups = ref({
  servicecenter: true,
  drehplatz: false,
  parkplatz: false
})

// Computed properties for grouped locations
const serviceLocations = computed(() => 
  availableLocations.value.filter(loc => {
    const name = loc.location_type?.name?.toLowerCase() || ''
    const slug = loc.location_type?.slug?.toLowerCase() || ''
    return name === 'servicecenter' || slug === 'servicecenter' || 
           name === 'service-center' || slug === 'service-center'
  })
)

const drehplatzLocations = computed(() => 
  availableLocations.value.filter(loc => {
    const name = loc.location_type?.name?.toLowerCase() || ''
    const slug = loc.location_type?.slug?.toLowerCase() || ''
    return name === 'drehplatz' || slug === 'drehplatz' || 
           name === 'dreh-platz' || slug === 'dreh-platz'
  })
)

const parkplatzLocations = computed(() => 
  availableLocations.value.filter(loc => {
    const name = loc.location_type?.name?.toLowerCase() || ''
    const slug = loc.location_type?.slug?.toLowerCase() || ''
    return name === 'parkplatz' || slug === 'parkplatz' || 
           name === 'parkplaetze' || slug === 'parkplaetze' ||
           name.includes('park')
  })
)

const selectedLocation = computed(() => 
  availableLocations.value.find(loc => loc.id === selectedLocationId.value)
)

// Computed property for button visibility - ensures proper reactivity
const canShowCompleteButton = computed(() => {
  const hasLocations = availableLocations.value && availableLocations.value.length > 0
  const notLoading = !isLoading.value
  
  // Debug logging to track button visibility
  if (!notLoading) {
    console.log('Complete button hidden: Still loading')
  } else if (!hasLocations) {
    console.log('Complete button hidden: No locations available', {
      availableLocations: availableLocations.value,
      count: availableLocations.value?.length || 0
    })
  } else {
    console.log('Complete button should be visible', {
      locationCount: availableLocations.value.length,
      isLoading: isLoading.value
    })
  }
  
  return notLoading && hasLocations
})

// Computed property to check if we have data but button is hidden (for debugging)
const hasDataButNoButton = computed(() => {
  return !isLoading.value && 
         availableLocations.value.length === 0 && 
         (serviceLocations.value.length > 0 || 
          drehplatzLocations.value.length > 0 || 
          parkplatzLocations.value.length > 0)
})

// Toggle group expansion
const toggleGroup = (group: 'servicecenter' | 'drehplatz' | 'parkplatz') => {
  expandedGroups.value[group] = !expandedGroups.value[group]
}

// Filter locations based on search query
const filterLocations = () => {
  if (!searchQuery.value) {
    filteredLocations.value = []
    return
  }
  
  const query = searchQuery.value.toLowerCase()
  filteredLocations.value = availableLocations.value.filter(location => 
    location.name.toLowerCase().includes(query) ||
    location.location_type.name.toLowerCase().includes(query)
  )
}

// Load recent locations from localStorage
const loadRecentLocations = () => {
  const stored = localStorage.getItem('recentCheckInLocations')
  if (stored) {
    try {
      const recentIds = JSON.parse(stored) as number[]
      recentLocations.value = recentIds
        .map(id => availableLocations.value.find(loc => loc.id === id))
        .filter(Boolean) as Location[]
    } catch (e) {
      console.error('Error loading recent locations:', e)
    }
  }
}

// Save recent location to localStorage
const saveRecentLocation = (locationId: number) => {
  const stored = localStorage.getItem('recentCheckInLocations')
  let recentIds: number[] = []
  
  if (stored) {
    try {
      recentIds = JSON.parse(stored) as number[]
    } catch (e) {
      console.error('Error parsing recent locations:', e)
    }
  }
  
  // Remove if exists and add to front
  recentIds = recentIds.filter(id => id !== locationId)
  recentIds.unshift(locationId)
  
  // Keep only last 5
  recentIds = recentIds.slice(0, 5)
  
  localStorage.setItem('recentCheckInLocations', JSON.stringify(recentIds))
}

// Lade verfügbare Locations
const fetchLocations = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    // Hole den aktuellen Benutzer mit Airport-Daten
    const userResponse = await api.get('/api/auth/me')
    console.log('User Response:', userResponse.data)
    
    // Handle multiple possible response structures
    let userData = null
    let locations = null
    
    // Check for response.data.user structure (most common)
    if (userResponse.data?.user) {
      userData = userResponse.data.user
      console.log('Found user data in response.data.user')
    } else if (userResponse.data) {
      // Fallback: data might be the user object directly
      userData = userResponse.data
      console.log('Using response.data as user data directly')
    }
    
    // Check for locations in various possible paths
    if (userData?.airport?.locations) {
      locations = userData.airport.locations
      console.log('Found locations in userData.airport.locations:', locations)
    } else if (userData?.locations) {
      locations = userData.locations
      console.log('Found locations in userData.locations:', locations)
    }
    
    // Verify we have locations
    if (!locations || !Array.isArray(locations) || locations.length === 0) {
      console.error('No locations found. User data structure:', userData)
      error.value = 'Keine Locations im Airport gefunden.'
      return
    }
    
    // Debug: Show all locations before filtering
    console.log('All locations before filtering:', locations.length, 'locations')
    if (locations.length > 0) {
      console.log('Sample location structure:', locations[0])
    }
    
    // Filtere nur die relevanten Location-Typen
    // IMPORTANT: Check both 'name' and 'slug' fields as different endpoints may use different fields
    availableLocations.value = locations.filter((location: Location) => {
      if (!location.location_type) {
        console.warn('Location without type:', location)
        return false
      }
      
      const typeName = location.location_type.name?.toLowerCase() || ''
      const typeSlug = location.location_type.slug?.toLowerCase() || ''
      
      // List of valid location types we want to show
      const validTypes = ['drehplatz', 'servicecenter', 'parkplatz', 'parkplaetze', 'service-center', 'dreh-platz']
      
      // Check if either name or slug matches any valid type
      const isValidType = validTypes.some(validType => 
        typeName.includes(validType) || typeSlug.includes(validType) ||
        validType.includes(typeName) || validType.includes(typeSlug)
      )
      
      if (!isValidType && location.location_type) {
        console.log(`Filtered out: ${location.name} (type: name='${typeName}', slug='${typeSlug}')`)
      }
      
      return isValidType
    })
    
    console.log('Filtered locations:', availableLocations.value.length, 'locations')
    if (availableLocations.value.length === 0 && locations.length > 0) {
      console.warn('No locations matched the filter! Check location types:', 
        locations.map((l: any) => ({ 
          name: l.name, 
          type_name: l.location_type?.name, 
          type_slug: l.location_type?.slug 
        }))
      )
    }
    
    if (availableLocations.value.length === 0) {
      error.value = 'Keine passenden Locations gefunden.'
    } else {
      // Clear any previous errors since we have locations
      error.value = null
      loadRecentLocations()
      
      // Auto-expand the group with most locations
      if (parkplatzLocations.value.length > 10) {
        expandedGroups.value.parkplatz = false
        expandedGroups.value.servicecenter = true
      }
    }
  } catch (err: any) {
    console.error('Fehler beim Laden der Locations:', err)
    error.value = 'Die Locations konnten nicht geladen werden. Bitte versuche es später erneut.'
  } finally {
    isLoading.value = false
    
    // Final state logging for debugging
    console.log('=== FINAL STATE AFTER FETCHING LOCATIONS ===')
    console.log('isLoading:', isLoading.value)
    console.log('availableLocations count:', availableLocations.value.length)
    console.log('serviceLocations count:', serviceLocations.value.length)
    console.log('drehplatzLocations count:', drehplatzLocations.value.length)
    console.log('parkplatzLocations count:', parkplatzLocations.value.length)
    console.log('error:', error.value)
    console.log('canShowCompleteButton will be:', !isLoading.value && availableLocations.value.length > 0)
    console.log('=============================================')
  }
}

// Schließe den Check-In ab
const completeCheckIn = async () => {
  if (!selectedLocationId.value) return
  
  isSubmitting.value = true
  
  try {
    await api.post(`/api/checker/checkins/${checkId.value}/complete`, {
      forwarded_to_location_id: selectedLocationId.value
    })
    
    // Save to recent locations
    saveRecentLocation(selectedLocationId.value)
    
    // Weiterleitung zum Dashboard
    router.push('/dashboard')
  } catch (err: any) {
    console.error('Fehler beim Abschließen des Check-Ins:', err)
    error.value = 'Der Check-In konnte nicht abgeschlossen werden. Bitte versuche es später erneut.'
  } finally {
    isSubmitting.value = false
  }
}

// Hilfsfunktion für die Anzeige der Location-Typen
const getLocationTypeName = (locationType: { id: number; name: string }) => {
  const typeNames: Record<string, string> = {
    'drehplatz': 'Drehplatz',
    'servicecenter': 'Servicecenter',
    'parkplatz': 'Parkplatz'
  }
  return typeNames[locationType.name.toLowerCase()] || locationType.name
}

onMounted(() => {
  fetchLocations()
  // Start heartbeat to keep checkin alive
  if (checkinIdNumber.value) {
    startHeartbeat()
  }
})

onUnmounted(() => {
  stopHeartbeat()
})
</script> 